import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { getRiderSectionValues, getRiderSectionChanges, validateRiderSectionChanges, riderEditSections, type RiderEditSection } from './riderProfileEditing';
import { Rider } from '../../services';
import {
  type RiderKYCDetailsUpdateData,
  type RiderProfileUpdateData,
} from '../../services/riderService';

interface RiderProfileTabProps {
  rider: Rider;
  onUpdateProfile?: (data: RiderProfileUpdateData) => Promise<void>;
  onUpdateKYCDetails?: (data: RiderKYCDetailsUpdateData) => Promise<void>;
}

const RiderProfileTab: React.FC<RiderProfileTabProps> = ({
  rider,
  onUpdateProfile,
  onUpdateKYCDetails,
}) => {
  const [section, setSection] = useState<RiderEditSection | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [initialForm, setInitialForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const formatDate = (value: string) => new Date(value).toLocaleDateString('en-IN');

  const openEditor = (nextSection: RiderEditSection) => {
    const values = getRiderSectionValues(rider, nextSection);
    setForm(values);
    setInitialForm(values);
    setError('');
    setSection(nextSection);
  };

  const editButton = (target: RiderEditSection) => {
    const supported = riderEditSections[target].kyc ? onUpdateKYCDetails : onUpdateProfile;
    return supported && (
      <Button size="small" variant="outlined" startIcon={<EditIcon />}
        aria-label={`Edit ${riderEditSections[target].title}`} onClick={() => openEditor(target)}>
        Edit
      </Button>
    );
  };

  const handleSave = async () => {
    if (!section || saving) return;
    const payload = getRiderSectionChanges(section, initialForm, form);
    const validationError = validateRiderSectionChanges(payload);
    if (validationError) { setError(validationError); return; }
    if (!Object.keys(payload).length) return;
    setSaving(true);
    setError('');
    try {
      if (riderEditSections[section].kyc) {
        await onUpdateKYCDetails?.(payload);
      } else {
        await onUpdateProfile?.(payload);
      }
      setSection(null);
    } catch (failure: any) {
      setError(failure.response?.data?.message || failure.message || 'Unable to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to extract document number from URL or return as-is
  const extractDocumentNumber = (value: string | null): string => {
    if (!value) return 'Not provided';

    // If it's a URL, it's likely an S3 link - show message to update in KYC tab
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return 'Please add document number in KYC tab';
    }

    // Otherwise, return the value as-is (it's the actual document number)
    return value;
  };

  return (
    <Box sx={{ px: 3, pb: 3 }}>
      <Grid container spacing={4}>
        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">Personal Information</Typography>
                {editButton('personal')}
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Full Name
                  </Typography>
                  <Typography variant="body1">{rider.name || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date of Birth
                  </Typography>
                  <Typography variant="body1">
                    {rider.dob ? formatDate(rider.dob) : 'Not provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Blood Group
                  </Typography>
                  <Typography variant="body1">{rider.blood_group || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Client Rider ID
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {rider.clientRiderId || 'Not provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Client Name
                  </Typography>
                  <Typography variant="body1">{rider.clientName || 'Not provided'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">Contact Information</Typography>
                {editButton('contact')}
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone Number
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body1">{rider.phone}</Typography>
                    {rider.phoneVerified && (
                      <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                      {rider.email || 'Not provided'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Document Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">Document Information</Typography>

              </Box>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <DescriptionIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Aadhar Card
                    </Typography>
                    <Box sx={{ ml: 'auto' }}>{editButton('aadhaar')}</Box>
                  </Box>
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      fontFamily: 'monospace',
                      color: rider.aadharNumber?.startsWith('http') ? 'warning.main' : 'text.primary',
                      userSelect: 'text',
                      cursor: 'text',
                      pointerEvents: 'none',
                      pl: 4,
                      fontStyle: rider.aadharNumber?.startsWith('http') ? 'italic' : 'normal'
                    }}
                  >
                    {extractDocumentNumber(rider.aadharNumber)}
                  </Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <DescriptionIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      PAN Card
                    </Typography>
                    <Box sx={{ ml: 'auto' }}>{editButton('pan')}</Box>
                  </Box>
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      fontFamily: 'monospace',
                      color: rider.panNumber?.startsWith('http') ? 'warning.main' : 'text.primary',
                      userSelect: 'text',
                      cursor: 'text',
                      pointerEvents: 'none',
                      pl: 4,
                      fontStyle: rider.panNumber?.startsWith('http') ? 'italic' : 'normal'
                    }}
                  >
                    {extractDocumentNumber(rider.panNumber)}
                  </Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <DescriptionIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Driving License
                    </Typography>
                    <Box sx={{ ml: 'auto' }}>{editButton('licence')}</Box>
                  </Box>
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      fontFamily: 'monospace',
                      color: rider.drivingLicenseNumber?.startsWith('http') ? 'warning.main' : 'text.primary',
                      userSelect: 'text',
                      cursor: 'text',
                      pointerEvents: 'none',
                      pl: 4,
                      fontStyle: rider.drivingLicenseNumber?.startsWith('http') ? 'italic' : 'normal'
                    }}
                  >
                    {extractDocumentNumber(rider.drivingLicenseNumber)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Address Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">Address Information</Typography>
                {editButton('address')}
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Address
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                    <Typography variant="body1">
                      {[rider.address1, rider.address2].filter(Boolean).join(', ') ||
                        'Not provided'}
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      City
                    </Typography>
                    <Typography variant="body1">{rider.city || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      State
                    </Typography>
                    <Typography variant="body1">{rider.state || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      PIN Code
                    </Typography>
                    <Typography variant="body1">{rider.pincode || 'Not provided'}</Typography>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Emergency Contact */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">Emergency Contact</Typography>
                {editButton('emergency')}
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">{rider.emergencyName || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">{rider.emergencyPhone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Relation
                  </Typography>
                  <Typography variant="body1">
                    {rider.emergencyRelation || 'Not provided'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={section !== null} onClose={() => { if (!saving) setSection(null); }} maxWidth="sm" fullWidth
        aria-labelledby="rider-section-edit-title">
        <DialogTitle id="rider-section-edit-title">Edit {section && riderEditSections[section].title}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ pt: 1 }}>
            {section && riderEditSections[section].fields.map(([field, label]) => (
              <TextField key={field} fullWidth size="small" label={label}
                type={field === 'dob' ? 'date' : field === 'email' ? 'email' : field === 'phone' || field === 'emergencyPhone' ? 'tel' : 'text'}
                value={form[field] || ''} disabled={saving || riderEditSections[section].readOnlyFields?.includes(field)}
                helperText={riderEditSections[section].readOnlyFields?.includes(field) ? 'Managed through client mappings' : undefined}
                InputLabelProps={field === 'dob' ? { shrink: true } : undefined}
                onChange={(event) => {
                  const value = event.target.value;
                  setForm((prev) => ({ ...prev, [field]: field === 'panNumber' || field === 'drivingLicenseNumber' ? value.toUpperCase() : value }));
                  setError('');
                }} />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSection(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave} disabled={saving || !section || !Object.keys(getRiderSectionChanges(section, initialForm, form)).length}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default RiderProfileTab;
